import { useState } from 'react';
import { Link } from 'react-router-dom';

const serviceLinks = [
  { label: 'Photography Studios', href: '/services' },
  { label: 'Video Production', href: '/services' },
  { label: 'Podcast Studios', href: '/services' },
  { label: 'Content Creation', href: '/services' },
  { label: 'Event Spaces', href: '/services' },
];

const locationLinks = [
  { label: 'New York City', href: '/locations' },
  { label: 'Los Angeles', href: '/locations' },
  { label: 'Chicago', href: '/locations' },
  { label: 'Miami', href: '/locations' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    const body = new URLSearchParams();
    body.append('email', email);
    await fetch('https://readdy.ai/api/form/d6uk0r6dp0k8rarn7lpg', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
    setSubmitted(true);
    setEmail('');
  };

  return (
    <footer className="bg-stone-950 text-stone-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center mb-5 cursor-pointer">
              <img
                src="https://storage.readdy-site.link/project_files/82416851-1135-4bfe-8ce6-550ef563351f/b3853fc3-6fd9-4dd8-b884-cccfadedee77_MyStudio_Logo_Studio.png?v=cab6c10ee6061b438c10513746d93a4d"
                alt="Book My Studio Space"
                className="h-9 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-stone-400 leading-relaxed mb-6">
              Professional studio spaces for photographers, videographers, podcasters, and content creators. Book flexibly — by the hour, half-day, or full day.
            </p>
            <div className="flex items-center gap-3">
              {['ri-instagram-line', 'ri-facebook-line', 'ri-twitter-x-line', 'ri-youtube-line'].map((icon) => (
                <a
                  key={icon}
                  href="#"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-stone-800 hover:bg-amber-500 text-stone-400 hover:text-white transition-all cursor-pointer"
                >
                  <i className={`${icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-5">Our Studios</h4>
            <ul className="space-y-3">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-stone-400 hover:text-amber-400 transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-5">Locations</h4>
            <ul className="space-y-3">
              {locationLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-stone-400 hover:text-amber-400 transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-4">Contact</h4>
              <div className="space-y-2">
                <a href="tel:+18005551234" className="flex items-center gap-2 text-sm text-stone-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-phone-line text-amber-500 flex-shrink-0" />
                  (800) 555-1234
                </a>
                <a href="mailto:hello@bookmystudiospace.com" className="flex items-center gap-2 text-sm text-stone-400 hover:text-amber-400 transition-colors cursor-pointer">
                  <i className="ri-mail-line text-amber-500 flex-shrink-0" />
                  hello@bookmystudio.com
                </a>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white text-sm font-semibold uppercase tracking-widest mb-5">Stay Updated</h4>
            <p className="text-sm text-stone-400 leading-relaxed mb-5">
              Get new studio openings, special rates, and creator tips delivered to your inbox.
            </p>
            {submitted ? (
              <div className="flex items-center gap-2 text-sm text-amber-400">
                <i className="ri-check-circle-line" />
                You&apos;re subscribed!
              </div>
            ) : (
              <form
                onSubmit={handleNewsletterSubmit}
                data-readdy-form
                className="flex flex-col gap-3"
              >
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-stone-800 border border-stone-700 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-amber-500 transition-colors"
                />
                <button
                  type="submit"
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-stone-500">
            &copy; {new Date().getFullYear()} Book My Studio Space. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
              <a key={item} href="#" className="text-xs text-stone-500 hover:text-stone-300 transition-colors cursor-pointer whitespace-nowrap">
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
